// Comment thread with @mentions, mounted in the invoice detail modal. Renders
// the invoice's running comment list (author + relative time + body), a composer
// with an @-mention typeahead over the directory, and a per-record "mentioned
// you" indicator.
//
// Posting sends only the body and the mention list: the API stamps the author
// and gives the comment the invoice's own visibility, so the client never names
// an owner. Mentions are a real string[] (they were a semicolon-delimited
// string), and the mention notification email is the API's job — this captures
// the mentions and shows the in-app indicator.
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Avatar,
  Badge,
  Body1,
  Button,
  Caption1,
  Spinner,
  Textarea,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { SendRegular } from '@fluentui/react-icons'
import type { Conversation, CurrentUser, DirectoryUser } from '../lib/types'
import { loadConversations, createConversation, searchPeople } from '../lib/data'
import { errorMessage } from '../api/errors'
import { formatRelativeTime } from '../lib/format'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    paddingTop: '4px',
  },
  head: { display: 'flex', alignItems: 'center', gap: '10px' },
  headTitle: { fontWeight: tokens.fontWeightSemibold },
  list: { display: 'flex', flexDirection: 'column', gap: '12px' },
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  comment: { display: 'flex', gap: '10px', alignItems: 'flex-start' },
  commentBody: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: 1 },
  commentMeta: { display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' },
  author: { fontWeight: tokens.fontWeightSemibold, fontSize: '13px' },
  time: { color: tokens.colorNeutralForeground3, fontSize: '11px' },
  text: { fontSize: '13px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  composer: { display: 'flex', flexDirection: 'column', gap: '6px' },
  // Relative so the mention menu anchors just below the textarea.
  taWrap: { position: 'relative' },
  textarea: { width: '100%' },
  composerRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  mentionHint: { flex: 1, color: tokens.colorNeutralForeground3, fontSize: '11px' },
  error: { color: tokens.colorPaletteRedForeground1 },
  // Floating @-mention typeahead. Not portaled (renders inside this component's
  // DOM) so it stays inside the modal's focus trap — the same reason the pickers
  // use inlinePopup (EntityPicker).
  menu: {
    position: 'absolute',
    top: '100%',
    left: '0',
    marginTop: '4px',
    zIndex: 100,
    minWidth: '260px',
    maxWidth: '100%',
    maxHeight: '220px',
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    padding: '4px',
  },
  menuItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    padding: '6px 10px',
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'pointer',
  },
  menuItemActive: { backgroundColor: tokens.colorNeutralBackground1Selected },
  menuItemName: { fontSize: '13px', fontWeight: tokens.fontWeightMedium },
  menuItemMail: { fontSize: '11px', color: tokens.colorNeutralForeground3 },
  menuHint: { padding: '6px 10px', color: tokens.colorNeutralForeground3, fontSize: '12px' },
})

interface Props {
  /** The invoice this thread hangs off. */
  parentId: string
  /** Signed-in user. Optional so a read-only surface can still show the thread;
      without it the composer + indicator are hidden. */
  user?: CurrentUser
}

interface PickedMention {
  /** The directory address written to `conversations.mentions`. */
  id: string
  /** Display name as inserted into the body (@name). */
  name: string
}

interface MentionState {
  query: string
  /** Index of the '@' in the body. */
  start: number
  /** Caret position when the token was last read. */
  caret: number
}

// True if `@name` still appears in the body as a whole mention — the char after
// the name must be absent or a non-word char, so "@John" isn't kept live by
// "@Johnathan". Names contain spaces, so a word-boundary on the full name holds.
function mentionIsLive(body: string, name: string): boolean {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`@${escaped}(?![\\p{L}\\p{N}])`, 'u').test(body)
}

// The in-progress @-token immediately left of the caret, or null. A mention
// token is '@' at the start or after whitespace, followed by non-whitespace.
function findMention(value: string, caret: number): MentionState | null {
  const upto = value.slice(0, caret)
  const m = /(?:^|\s)@(\S*)$/.exec(upto)
  if (!m) return null
  const query = m[1]
  return { query, start: caret - query.length - 1, caret }
}

export function CommentThread({ parentId, user }: Props) {
  const s = useStyles()
  const [comments, setComments] = useState<Conversation[] | 'loading'>('loading')
  const [body, setBody] = useState('')
  const [mentions, setMentions] = useState<PickedMention[]>([])
  const [posting, setPosting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Active @-mention token + typeahead results.
  const [mention, setMention] = useState<MentionState | null>(null)
  const [results, setResults] = useState<DirectoryUser[]>([])
  const [resultsLoading, setResultsLoading] = useState(false)
  const [highlight, setHighlight] = useState(0)

  const taRef = useRef<HTMLTextAreaElement | null>(null)
  const loadSeq = useRef(0)
  const searchSeq = useRef(0)

  const reload = useCallback(async () => {
    const seq = ++loadSeq.current
    if (!parentId) {
      setComments([])
      return
    }
    setComments('loading')
    try {
      const rows = await loadConversations(parentId)
      if (seq === loadSeq.current) setComments(rows)
    } catch (e) {
      if (seq !== loadSeq.current) return
      setComments([])
      setError(`Could not load comments: ${errorMessage(e)}`)
    }
  }, [parentId])

  useEffect(() => {
    reload()
  }, [reload])

  // Debounced directory search for the active @-token (seq-guarded so a slow
  // earlier response can't overwrite a newer one).
  useEffect(() => {
    if (!mention) {
      setResults([])
      setResultsLoading(false)
      return
    }
    const my = ++searchSeq.current
    setResultsLoading(true)
    const t = setTimeout(() => {
      searchPeople(mention.query, 8)
        .then((res) => {
          if (my !== searchSeq.current) return
          setResults(res)
          setHighlight(0)
        })
        .catch(() => {
          if (my === searchSeq.current) setResults([])
        })
        .finally(() => {
          if (my === searchSeq.current) setResultsLoading(false)
        })
    }, 200)
    return () => clearTimeout(t)
  }, [mention])

  function onChange(value: string, el: HTMLTextAreaElement) {
    setBody(value)
    const caret = el.selectionStart ?? value.length
    setMention(findMention(value, caret))
  }

  function selectMention(u: DirectoryUser) {
    if (!mention) return
    const name = u.display_name || u.mail || u.upn || 'user'
    const insert = `@${name} `
    const next = body.slice(0, mention.start) + insert + body.slice(mention.caret)
    setBody(next)
    // Store the directory address, because that is what the profile's
    // directory_email is compared against by the mentioned-you indicator.
    const id = u.mail || u.upn
    if (id) {
      setMentions((prev) =>
        prev.some((m) => m.id.toLowerCase() === id.toLowerCase()) ? prev : [...prev, { id, name }],
      )
    }
    setMention(null)
    setResults([])
    // Restore the caret to just after the inserted mention on the next frame,
    // once the new value has rendered.
    const newCaret = mention.start + insert.length
    requestAnimationFrame(() => {
      const ta = taRef.current
      if (ta) {
        ta.focus()
        ta.setSelectionRange(newCaret, newCaret)
      }
    })
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!mention || results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => (h + 1) % results.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => (h - 1 + results.length) % results.length)
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      selectMention(results[highlight] ?? results[0])
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setMention(null)
    }
  }

  async function handlePost() {
    const text = body.trim()
    if (!text || !user || posting) return
    setPosting(true)
    setError(null)
    try {
      // Keep only mentions whose @name is still present in the body (the user may
      // have deleted the text after picking). Dedupe by identifier.
      const ids = [
        ...new Set(
          mentions
            .filter((m) => mentionIsLive(body, m.name))
            .map((m) => m.id),
        ),
      ]
      // The API stamps the author and the comment's visibility from the
      // invoice, so the body and the mention list are the whole payload.
      await createConversation(parentId, text, ids)
      setBody('')
      setMentions([])
      setMention(null)
      await reload()
    } catch (e) {
      setError(`Could not post the comment: ${errorMessage(e)}`)
    } finally {
      setPosting(false)
    }
  }

  // Per-record "mentioned you": any comment whose mention list carries the
  // signed-in user's directory address (case-insensitive). That address — not the
  // user principal name — is what a mention stores, and the two can differ.
  const myIds = [user?.directoryEmail, user?.email]
    .filter((v): v is string => !!v)
    .map((v) => v.toLowerCase())
  const mentionedMe =
    comments !== 'loading' &&
    myIds.length > 0 &&
    comments.some((c) =>
      (c.mentions ?? [])
        .map((x) => x.trim().toLowerCase())
        .filter(Boolean)
        .some((m) => myIds.includes(m)),
    )

  const liveMentions = mentions.filter((m) => mentionIsLive(body, m.name))

  return (
    <div className={s.root}>
      <div className={s.head}>
        <span className={s.headTitle}>Comments</span>
        {comments !== 'loading' && comments.length > 0 && (
          <Caption1>({comments.length})</Caption1>
        )}
        {mentionedMe && (
          <Badge appearance="tint" color="brand" size="small">
            Mentioned you
          </Badge>
        )}
        {comments === 'loading' && <Spinner size="tiny" />}
      </div>

      {comments !== 'loading' && (
        <div className={s.list}>
          {comments.length === 0 ? (
            <Body1 className={s.none}>No comments yet.</Body1>
          ) : (
            comments.map((c) => {
              const author = c.author_name || c.author_email || 'Unknown'
              return (
                <div key={c.id} className={s.comment}>
                  <Avatar name={author} size={28} color="colorful" />
                  <div className={s.commentBody}>
                    <div className={s.commentMeta}>
                      <span className={s.author}>{author}</span>
                      <span className={s.time}>{formatRelativeTime(c.created_at)}</span>
                    </div>
                    <span className={s.text}>{c.body}</span>
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}

      {error && <Caption1 className={s.error}>{error}</Caption1>}

      {user && (
        <div className={s.composer}>
          <div className={s.taWrap}>
            <Textarea
              textarea={{ ref: taRef }}
              className={s.textarea}
              placeholder="Add a comment… Type @ to mention someone."
              value={body}
              resize="vertical"
              rows={2}
              disabled={posting}
              onChange={(e, d) => onChange(d.value, e.target as HTMLTextAreaElement)}
              onKeyDown={onKeyDown}
              onBlur={() => setMention(null)}
            />

            {mention && (
              <div className={s.menu}>
                {resultsLoading && (
                  <div className={s.menuHint}>
                    <Spinner size="tiny" label="Searching people…" />
                  </div>
                )}
                {!resultsLoading && results.length === 0 && (
                  <div className={s.menuHint}>No people found.</div>
                )}
                {results.map((u, i) => (
                  <div
                    key={u.id}
                    className={mergeClasses(s.menuItem, i === highlight && s.menuItemActive)}
                    // Pick on mousedown so the textarea keeps focus (preventDefault
                    // stops the blur that would otherwise close the menu first).
                    onMouseDown={(e) => {
                      e.preventDefault()
                      selectMention(u)
                    }}
                    onMouseEnter={() => setHighlight(i)}
                  >
                    <span className={s.menuItemName}>{u.display_name || u.mail || u.upn || 'Unknown'}</span>
                    {(u.mail || u.upn) && <span className={s.menuItemMail}>{u.mail || u.upn}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={s.composerRow}>
            <span className={s.mentionHint}>
              {liveMentions.length > 0
                ? `Mentioning: ${liveMentions.map((m) => m.name).join(', ')}`
                : 'Type @ to mention a teammate.'}
            </span>
            <Button
              appearance="primary"
              icon={<SendRegular />}
              disabled={!body.trim() || posting}
              onClick={handlePost}
            >
              {posting ? 'Posting…' : 'Post'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
